/// <amd-module name="Artifacts/Connect/redux/Sagas" />
import { dismissedToolsKey } from "Artifacts/Connect/Constants";
import { ConnectToFeedActions, ConnectToFeedActionTypes } from "Artifacts/Connect/redux/Actions";
import { ConnectToFeedData } from "Artifacts/Connect/redux/Contracts";
import { ToolId } from "Artifacts/Connect/types/ITool";
import { CommonConstants } from "Artifacts/Root/Constants";
import { getContext } from "Artifacts/Root/Saga";
import { IFeed } from "Artifacts/Root/types/IFeed";
import { SagaIterator } from "redux-saga";
import { effects } from "redux/redux-saga.compat";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { IVssPageContext } from "VSS/Platform/Context";
import { getServiceLocation, routeUrl } from "VSS/Platform/Location";
import { IPageData } from "VSS/Platform/Page";
import * as Storage from "VSS/Platform/Util/Storage";
import { combineUrlPaths, Uri } from "VSS/Platform/Util/Url";
import { getVssPageData } from "VSS/Redux/Modules/VSSPageData";

export default function* rootSaga(): SagaIterator {
  yield effects.takeEvery(ConnectToFeedActionTypes.LoadInitialData, loadInitialData);
  yield effects.takeEvery(ConnectToFeedActionTypes.DismissFirstTimeMessage, handleMessageDismissed);
}

export function* loadInitialData(
  action: ActionWithPayload<ConnectToFeedActionTypes.LoadInitialData, IFeed>,
): SagaIterator {
  try {
    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const feed = action.payload;

    yield effects.put(ConnectToFeedActions.loadInitialDataStarted());

    const storedMessageDismissedTools: ToolId[] =
      (yield effects.call(Storage.tryGetLocalStorageObject, dismissedToolsKey)) || [];

    const packagingUrl = yield* getPackagingUrl(
      pageContext,
      feed.accountMetadata.publicAccessMapping,
    );
    const sessionTokenUri: Uri = yield* getSessionTokenUrl(pageContext);

    const connectToFeedData: ConnectToFeedData = {
      dismissedTools: storedMessageDismissedTools,
      packagingUrl,
      sessionTokenUri,
    };

    yield effects.put(ConnectToFeedActions.initializeConnectToFeedState(connectToFeedData));
  } catch (error) {
    yield effects.put(ConnectToFeedActions.loadInitialDataFailed(error.message));
  }
}

function* getSessionTokenUrl(pageContext: IVssPageContext): SagaIterator {
  const rawUrl = (yield effects.call(
    routeUrl,
    pageContext,
    "ms.vss-tfs-web.user-settings-tokens-default-route",
  )) as any;
  return new Uri(rawUrl);
}

function* getPackagingUrl(pageContext: IVssPageContext, publicAccessMapping: string): SagaIterator {
  // service location for onprem is /tfs/DefaultCollection/ and hosted is https://dev.azure.com/<orgname>/
  const url: string = getServiceLocation(pageContext, CommonConstants.PackagingServiceInstanceId);

  const vssPageData: IPageData = (yield effects.select(getVssPageData)) as any;
  if (vssPageData.isHosted) {
    return new Uri(url).absoluteUri;
  } else {
    // in onprem, host is not returned so use the public access mapping in dataprovider to construct url
    const publicAccessMappingUrl = new Uri(publicAccessMapping);

    publicAccessMappingUrl.path = "";
    publicAccessMappingUrl.queryString = "";
    publicAccessMappingUrl.hashString = "";

    return combineUrlPaths(publicAccessMappingUrl.absoluteUri, url);
  }
}

export function* handleMessageDismissed(
  action: ActionWithPayload<ConnectToFeedActionTypes.DismissFirstTimeMessage, ToolId>,
): SagaIterator {
  const dismissedToolIds: ToolId[] =
    (yield effects.call(Storage.tryGetLocalStorageObject, dismissedToolsKey)) || [];

  const toolId: ToolId = action.payload;

  if (dismissedToolIds.indexOf(toolId) === -1) {
    dismissedToolIds.push(toolId);
    yield effects.call(Storage.trySetLocalStorageObject, dismissedToolsKey, dismissedToolIds);
  }
}
